/**
 * 切换员工身份弹框组件
 * 用于选择可选单位并切换员工身份
 */

import { Card, Form, Modal, message, Radio } from 'antd';
import React, { useEffect } from 'react';
import {
  getPlatV1AccessSelf,
  getPlatV1AccessStaffList,
  postPlatV1AccessSwitchStaff,
} from '@/services/swagger/pingtaijiekou';

interface StaffSwitchModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface StaffListItem {
  orgStaffId?: string;
  userId?: string;
  orgId?: string;
  orgName?: string;
  deptId?: string;
  deptName?: string;
  postId?: string;
  postName?: string;
  roleId?: string;
  roleName?: string;
  actived?: string;
  enabled?: string;
}

const StaffSwitchModal: React.FC<StaffSwitchModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [staffList, setStaffList] = React.useState<StaffListItem[]>([]);
  const [currentStaffId, setCurrentStaffId] = React.useState<string>();
  const [selectedStaffId, setSelectedStaffId] = React.useState<string>();
  const [currentOrgId, setCurrentOrgId] = React.useState<string>();

  // 获取当前用户信息和可选单位列表
  useEffect(() => {
    if (visible) {
      loadCurrentUserInfo();
      loadStaffList();
    }
  }, [visible]);

  // 获取当前用户信息，得到当前选中的员工 ID 和组织 ID
  const loadCurrentUserInfo = async () => {
    try {
      const response = await getPlatV1AccessSelf({});
      if (response?.staffId) {
        setCurrentStaffId(response.staffId);
        setSelectedStaffId(response.staffId);
      }
      if (response?.orgId) {
        setCurrentOrgId(response.orgId);
      }
    } catch (error: any) {
      console.error('获取当前用户信息失败:', error);
    }
  };

  const loadStaffList = async () => {
    try {
      setLoading(true);
      const response = await getPlatV1AccessStaffList({});

      if (response?.list) {
        setStaffList(response.list);
      }
    } catch (error: any) {
      console.error('获取可选单位列表失败:', error);
      // 🔒 移除 message.error()，让全局 errorHandler 统一处理错误
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedStaffId) {
        message.warning('请选择要切换的单位');
        return;
      }

      if (selectedStaffId === currentStaffId) {
        message.info('当前已在目标单位，无需切换');
        onCancel();
        return;
      }

      setLoading(true);

      // 调用切换员工身份接口
      await postPlatV1AccessSwitchStaff({
        staffId: selectedStaffId,
      });

      message.success('切换员工身份成功');
      onSuccess();
    } catch (error: any) {
      console.error('切换员工身份失败:', error);
      // 🔒 移除 message.error()，让全局 errorHandler 统一处理错误
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="切换员工身份"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={700}
      okText="确认切换"
      cancelText="取消"
    >
      <div style={{ marginBottom: '16px', fontWeight: 500 }}>
        可选单位列表（共 {staffList.length} 个）：
      </div>

      {staffList.length > 0 ? (
        <Radio.Group
          value={selectedStaffId}
          onChange={(e) => setSelectedStaffId(e.target.value)}
          style={{ display: 'block', maxHeight: '400px', overflowY: 'auto' }}
        >
          {staffList.map((item) => (
            <Card
              key={item.orgStaffId}
              hoverable
              style={{
                marginBottom: '12px',
                border:
                  selectedStaffId === item.orgStaffId
                    ? '2px solid #1890ff'
                    : '1px solid #f0f0f0',
                opacity: item.enabled !== '1' ? 0.5 : 1,
                cursor: item.enabled !== '1' ? 'not-allowed' : 'pointer',
              }}
              onClick={() => {
                if (item.enabled === '1' && item.orgStaffId) {
                  setSelectedStaffId(item.orgStaffId);
                }
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <Radio
                      value={item.orgStaffId}
                      disabled={item.enabled !== '1'}
                    />
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: '15px',
                        color:
                          selectedStaffId === item.orgStaffId
                            ? '#1890ff'
                            : '#262626',
                      }}
                    >
                      {item.orgName}
                    </span>
                    {/* 当前所在标识：只有与当前组织 ID 匹配的员工才显示 */}
                    {currentOrgId && item.orgId === currentOrgId && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#1890ff',
                          background: '#e6f7ff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                        }}
                      >
                        当前所在
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      color: 'rgba(0, 0, 0, 0.65)',
                      fontSize: '13px',
                      marginLeft: '24px',
                    }}
                  >
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ marginRight: '16px' }}>
                        📋 部门：{item.deptName || '-'}
                      </span>
                      <span>💼 岗位：{item.postName || '-'}</span>
                    </div>
                    {item.roleName && <div>🎯 角色：{item.roleName}</div>}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color:
                      item.enabled !== '1'
                        ? '#999'
                        : currentOrgId && item.orgId === currentOrgId
                          ? '#1890ff'
                          : '#52c41a',
                  }}
                >
                  {item.enabled !== '1' ? (
                    <span>已禁用</span>
                  ) : currentOrgId && item.orgId === currentOrgId ? (
                    <span>● 当前</span>
                  ) : (
                    <span>○ 可切换</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </Radio.Group>
      ) : (
        !loading && (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#999',
            }}
          >
            暂无可选单位
          </div>
        )
      )}
    </Modal>
  );
};

export default StaffSwitchModal;
